import { useState } from 'react';
import { teamsAPI } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getLocalizedErrorMessage } from '@/lib/error-i18n';
import i18n from '@/lib/i18n';

export const useTeamInvite = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // 🔹 Generate invite link
  const generateInviteLink = async (teamId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await teamsAPI.generateInviteLink(teamId);

      if (response.success) {
        toast({
          title: i18n.t("common.success"),
          description: i18n.t("messages.inviteGenerated"),
        });

        return { 
          success: true, 
          data: response.data // { link, expiresAt }
        };
      } else {
        throw new Error(response.message || 'Failed to generate invite link');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.inviteFailed');
      setError(errorMessage);

      toast({
        title: i18n.t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });

      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  // 🔹 Join team via invite token
  const joinWithInvite = async (token: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await teamsAPI.joinWithInvite(token);

      if (response.success) {
        return {
          success: true,
          data: response.data,
          alreadyMember: response.alreadyMember || false,
          message: response.message,
        };
      } else {
        throw new Error(response.message || 'Failed to join team');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.inviteFailed');
      setError(errorMessage);

      toast({
        title: i18n.t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });

    return {
      success: false,
      error: errorMessage,
      code: err.code,
      status: err.status,
    };

    } finally {
      setIsLoading(false);
    }
  };

  const validateInvite = async (token: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await teamsAPI.validateInvite(token);

      if (response.success) {
        return {
          success: true,
          data: response.data, // { groupId, groupName, expiresAt }
        };
      } else {
        throw response;
      }
    } catch (err: any) {
      return {
        success: false,
        code: err.code,
        message: err.message,
        status: err.status,
      };
    } finally {
      setIsLoading(false);
    }
  };  

  const requestNewInvite = async (token: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await teamsAPI.requestNewInvite(token);

      if (response.success) {
        toast({
          title: "Request Sent",
          description: "Team owner will generate a new invite",
        });

        return { success: true };
      } else {
        throw response;
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.inviteFailed');

      toast({
        title: i18n.t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });

      return {
        success: false,
        code: err.code,
        message: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  };  

  return {
    isLoading,
    error,
    generateInviteLink,
    joinWithInvite,
    validateInvite,
    requestNewInvite,
  };


};
